import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Video, VideoOff, Mic, MicOff, PhoneOff, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';


// TURN server configuration - с несколькими протоколами для надёжности
const TURN_HOST = "72.56.252.213";
const TURN_USERNAME = "speeddate";
const TURN_CREDENTIAL = "SdTurn_2026!Live";

const ICE_SERVERS = {
  iceServers: [
    // STUN servers (free, for direct connections)
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    // TURN server - UDP (основной)
    {
      urls: 'turn:72.56.252.213:3478?transport=udp',
      username: TURN_USERNAME,
      credential: TURN_CREDENTIAL
    },
    // TURN server - TCP (для строгих NAT/firewall)
    {
      urls: 'turn:72.56.252.213:3478?transport=tcp',
      username: TURN_USERNAME,
      credential: TURN_CREDENTIAL
    }
  ],
  iceCandidatePoolSize: 10,
  iceTransportPolicy: 'all' // Используем все доступные транспорты
};

const RealVideoChat = ({ 
  matchUser, 
  sessionId,
  userId,
  socket,
  onTimeEnd,
  onEndChat,
  timeLeft,
  formatTime
}) => {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const containerRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const isCallerRef = useRef(false);
  const hasCreatedOfferRef = useRef(false);
  const pendingCandidatesRef = useRef([]);
  
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [connectionState, setConnectionState] = useState('connecting');
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Простой toggle для полноэкранного режима - мобильный CSS подход
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    
    const elem = containerRef.current;
    
    if (elem.classList.contains('mobile-fullscreen')) {
      // Выходим из полноэкранного режима
      elem.classList.remove('mobile-fullscreen');
      setIsFullscreen(false);
    } else {
      // Входим в полноэкранный режим
      elem.classList.add('mobile-fullscreen');
      setIsFullscreen(true);
    }
  }, []);

  // Initialize local media stream - максимальное качество с адаптацией
  const initLocalStream = useCallback(async () => {
    try {
      // Определяем тип устройства
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      console.log('Initializing media stream, iOS:', isIOS, 'Mobile:', isMobile);
      
      // Максимальные constraints - WebRTC сам адаптирует качество под скорость интернета
      const hdConstraints = {
        video: {
          facingMode: 'user',
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 },
          frameRate: { ideal: 30, min: 15 }
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      };
      
      let stream;
      try {
        console.log('Trying HD constraints...');
        stream = await navigator.mediaDevices.getUserMedia(hdConstraints);
        console.log('Got HD stream with tracks:', stream.getTracks().map(t => `${t.kind}:${t.readyState}`));
      } catch (firstErr) {
        console.warn('Failed with HD constraints:', firstErr.name, firstErr.message);
        
        // Fallback 1: стандартное качество
        try {
          console.log('Trying standard constraints...');
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
            audio: true
          });
        } catch (secondErr) {
          console.warn('Fallback 1 failed:', secondErr.name);
          
          // Fallback 2: минимальные constraints
          console.log('Trying minimal constraints...');
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true
          });
        }
      }
      
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        // Для мобильных нужно явно вызвать play() с обработкой ошибок
        try {
          await localVideoRef.current.play();
          console.log('Local video playing');
        } catch (playErr) {
          console.warn('Auto-play issue:', playErr.name);
          // Не бросаем ошибку - видео может начать воспроизводиться после взаимодействия
        }
      }
      
      return stream;
    } catch (err) {
      console.error('Error accessing media devices:', err.name, err.message);
      setError(`Не удалось получить доступ к камере: ${err.message}`);
      throw err;
    }
  }, []);

  // Create peer connection with optimized settings
  const createPeerConnection = useCallback(() => {
    const pc = new RTCPeerConnection(ICE_SERVERS);
    
    // Таймер для рестарта ICE если соединение зависло
    let iceRestartTimeout = null;
    
    pc.onicecandidate = (event) => {
      if (event.candidate && socket) {
        console.log('Sending ICE candidate:', event.candidate.type, event.candidate.protocol);
        socket.emit('ice_candidate', {
          session_id: sessionId,
          candidate: event.candidate
        });
      }
    };
    
    pc.onicegatheringstatechange = () => {
      console.log('ICE gathering state:', pc.iceGatheringState);
    };

    pc.oniceconnectionstatechange = () => {
      console.log('ICE connection state:', pc.iceConnectionState);
      setConnectionState(pc.iceConnectionState);
      
      // Очищаем таймер рестарта если соединение установлено
      if (iceRestartTimeout) {
        clearTimeout(iceRestartTimeout);
        iceRestartTimeout = null;
      }
      
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        // Соединение успешно - очищаем ошибку
        setError(null);
        // WebRTC автоматически адаптирует битрейт под скорость соединения
        // Не устанавливаем фиксированный maxBitrate - пусть адаптируется
      } else if (pc.iceConnectionState === 'checking') {
        // Устанавливаем таймаут 15 секунд для ICE checking
        iceRestartTimeout = setTimeout(() => {
          console.log('ICE checking timeout - attempting restart');
          if (pc.iceConnectionState === 'checking') {
            // Пробуем рестарт ICE
            pc.restartIce();
          }
        }, 15000);
      } else if (pc.iceConnectionState === 'failed') {
        console.log('ICE connection failed - attempting restart');
        setError('Соединение прервано, переподключение...');
        // Автоматический рестарт при сбое
        pc.restartIce();
      } else if (pc.iceConnectionState === 'disconnected') {
        // Даём 5 секунд на восстановление
        iceRestartTimeout = setTimeout(() => {
          if (pc.iceConnectionState === 'disconnected') {
            console.log('Still disconnected - attempting restart');
            pc.restartIce();
          }
        }, 5000);
      }
    };

    pc.ontrack = (event) => {
      console.log('Received remote track:', event.track.kind);
      if (remoteVideoRef.current && event.streams[0]) {
        remoteVideoRef.current.srcObject = event.streams[0];
        // Для мобильных - явно запускаем воспроизведение
        remoteVideoRef.current.play().catch(e => console.log('Remote video play error:', e));
        // Получили видео - очищаем ошибку
        setError(null);
      }
    };

    // Add local tracks to connection
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        console.log('Adding local track:', track.kind);
        pc.addTrack(track, localStreamRef.current);
      });
    }

    peerConnectionRef.current = pc;
    return pc;
  }, [socket, sessionId]);

  // Handle incoming offer (only for callee)
  const handleOffer = useCallback(async (offer) => {
    try {
      console.log('Handling offer, current signalingState:', peerConnectionRef.current?.signalingState);
      
      // Если мы уже создали offer (caller), игнорируем входящий offer
      if (isCallerRef.current && hasCreatedOfferRef.current) {
        console.log('Ignoring offer - we are the caller');
        return;
      }
      
      let pc = peerConnectionRef.current;
      if (!pc) {
        pc = createPeerConnection();
      }
      
      // Проверяем состояние - можно принять offer только в stable или have-local-offer
      if (pc.signalingState !== 'stable' && pc.signalingState !== 'have-local-offer') {
        console.log('Cannot handle offer in state:', pc.signalingState);
        return;
      }
      
      // Если у нас есть локальный offer и пришёл offer - это "glare" ситуация
      // Решаем через "polite peer" паттерн - один уступает
      if (pc.signalingState === 'have-local-offer') {
        // Callee уступает caller'у
        if (!isCallerRef.current) {
          console.log('Glare detected, rolling back as callee');
          await pc.setLocalDescription({type: 'rollback'});
        } else {
          console.log('Glare detected, ignoring as caller');
          return;
        }
      }
      
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      
      // Добавляем буферизованные ICE кандидаты
      while (pendingCandidatesRef.current.length > 0) {
        const candidate = pendingCandidatesRef.current.shift();
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (e) {
          console.error('Error adding buffered ICE candidate:', e);
        }
      }
      
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      if (socket) {
        socket.emit('answer', {
          session_id: sessionId,
          answer: answer
        });
      }
    } catch (err) {
      console.error('Error handling offer:', err);
      setError('Ошибка обработки входящего звонка');
    }
  }, [createPeerConnection, socket, sessionId]);

  // Handle incoming answer (only for caller)
  const handleAnswer = useCallback(async (answer) => {
    try {
      const pc = peerConnectionRef.current;
      if (!pc) {
        console.log('No peer connection for answer');
        return;
      }
      
      console.log('Handling answer, current signalingState:', pc.signalingState);
      
      // Answer можно установить только если мы в состоянии have-local-offer
      if (pc.signalingState !== 'have-local-offer') {
        console.log('Ignoring answer - not in have-local-offer state, current:', pc.signalingState);
        return;
      }
      
      await pc.setRemoteDescription(new RTCSessionDescription(answer));
      
      // Добавляем буферизованные ICE кандидаты
      while (pendingCandidatesRef.current.length > 0) {
        const candidate = pendingCandidatesRef.current.shift();
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (e) {
          console.error('Error adding buffered ICE candidate:', e);
        }
      }
    } catch (err) {
      console.error('Error handling answer:', err);
    }
  }, []);

  // Handle incoming ICE candidate
  const handleIceCandidate = useCallback(async (candidate) => {
    try {
      const pc = peerConnectionRef.current;
      if (!pc || !candidate) return;
      
      // Если remote description ещё не установлен, буферизуем кандидата
      if (!pc.remoteDescription || !pc.remoteDescription.type) {
        console.log('Buffering ICE candidate (no remote description yet)');
        pendingCandidatesRef.current.push(candidate);
        return;
      }
      
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (err) {
      console.error('Error adding ICE candidate:', err);
    }
  }, []);

  // Start the call (create offer) - ONLY for caller
  const startCall = useCallback(async () => {
    // Защита от повторного создания offer
    if (hasCreatedOfferRef.current) {
      console.log('Offer already created, skipping');
      return;
    }
    
    try {
      console.log('Starting call as CALLER');
      isCallerRef.current = true;
      hasCreatedOfferRef.current = true;
      
      let pc = peerConnectionRef.current;
      if (!pc) {
        pc = createPeerConnection();
      }
      
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      
      if (socket) {
        socket.emit('offer', {
          session_id: sessionId,
          offer: offer
        });
      }
    } catch (err) {
      console.error('Error starting call:', err);
      setError('Ошибка начала звонка');
      hasCreatedOfferRef.current = false;
    }
  }, [createPeerConnection, socket, sessionId]);

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoEnabled(videoTrack.enabled);
      }
    }
  }, []);

  // Toggle audio
  const toggleAudio = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioEnabled(audioTrack.enabled);
      }
    }
  }, []);

  // Cleanup
  const cleanup = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        console.log('Initializing video chat...');
        await initLocalStream();
        
        // Создаём peer connection сразу после получения медиа
        if (mounted) {
          createPeerConnection();
        }
        
        if (mounted && socket) {
          // Listen for WebRTC signals
          socket.on('offer', (data) => {
            console.log('Received offer from:', data.from);
            handleOffer(data.offer);
          });
          socket.on('answer', (data) => {
            console.log('Received answer from:', data.from);
            handleAnswer(data.answer);
          });
          socket.on('ice_candidate', (data) => {
            console.log('Received ICE candidate');
            handleIceCandidate(data.candidate);
          });
          
          // peer_joined - мы были первыми в комнате, новый пир присоединился
          // МЫ становимся CALLER и создаём offer
          socket.on('peer_joined', () => {
            console.log('Peer joined - WE are the CALLER, starting call...');
            isCallerRef.current = true;
            startCall();
          });
          
          // room_joined - мы присоединились к комнате
          // Если там уже есть пиры - ждём offer от них (мы CALLEE)
          socket.on('room_joined', (data) => {
            console.log('Room joined:', data);
            if (data.peers && data.peers.length > 0) {
              console.log('Peers already in room - WE are the CALLEE, waiting for offer...');
              isCallerRef.current = false;
              // НЕ создаём offer - ждём его от существующего пира
            } else {
              console.log('Room is empty, waiting for peer...');
            }
          });
          
          // Join the session room
          console.log('Joining room:', sessionId);
          socket.emit('join_room', { 
            room_id: sessionId,
            user_id: userId || 'user'
          });
        }
      } catch (err) {
        console.error('Init error:', err);
        setError('Не удалось подключиться к камере');
      }
    };

    // НЕ завершаем звонок при переключении вкладок - видео продолжает работать
    // Только при закрытии страницы/браузера завершаем
    const handleBeforeUnload = () => {
      if (onEndChat) {
        console.log('Page unloading - ending call');
        onEndChat();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    init();

    return () => {
      mounted = false;
      cleanup();
      // Сбрасываем состояние
      isCallerRef.current = false;
      hasCreatedOfferRef.current = false;
      pendingCandidatesRef.current = [];
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (socket) {
        socket.off('offer');
        socket.off('answer');
        socket.off('ice_candidate');
        socket.off('peer_joined');
        socket.off('room_joined');
      }
    };
  }, [initLocalStream, createPeerConnection, socket, sessionId, handleOffer, handleAnswer, handleIceCandidate, startCall, cleanup, onEndChat]);

  return (
    <div 
      ref={containerRef}
      className="relative bg-gradient-to-br from-[#2C2C54] to-[#474787] rounded-3xl overflow-hidden shadow-2xl border border-white/10"
    >
      {/* Remote video (full size) */}
      <div className="aspect-video bg-black/50 relative">
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          muted={false}
          webkit-playsinline="true"
          x5-playsinline="true"
          className="w-full h-full object-cover"
        />
        
        {/* Connection status overlay */}
        {connectionState !== 'connected' && connectionState !== 'completed' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <div className="text-center text-white">
              {(connectionState === 'connecting' || connectionState === 'new') && (
                <>
                  <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
                  <p>Подключение к собеседнику...</p>
                </>
              )}
              {connectionState === 'checking' && (
                <p>Проверка соединения...</p>
              )}
              {connectionState === 'failed' && (
                <p className="text-red-400">Соединение не удалось</p>
              )}
              {connectionState === 'disconnected' && (
                <p className="text-yellow-400">Соединение потеряно</p>
              )}
            </div>
          </div>
        )}

        {/* Match user info - уменьшенная версия */}
        {matchUser && (
          <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-black/50 backdrop-blur-sm text-white px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg">
            <p className="text-xs sm:text-sm font-medium">{matchUser.name}, {matchUser.age}</p>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="absolute top-2 left-28 sm:top-4 sm:left-40 bg-red-500/80 text-white px-3 py-1.5 rounded-lg text-xs">
            {error}
          </div>
        )}
      </div>

      {/* Local video (picture-in-picture) + Timer - в правом верхнем углу */}
      <div className="absolute top-2 right-2 sm:top-4 sm:right-4 flex flex-col items-center gap-1">
        <div className="w-20 h-16 sm:w-32 sm:h-24 rounded-lg overflow-hidden shadow-lg border-2 border-white/30 relative">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            webkit-playsinline="true"
            x5-playsinline="true"
            className="w-full h-full object-cover mirror"
            style={{ transform: 'scaleX(-1)' }}
          />
          {!isVideoEnabled && (
            <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
              <VideoOff className="w-6 h-6 text-white/50" />
            </div>
          )}
        </div>
        
        {/* Timer - под маленьким окном своего видео */}
        {timeLeft !== undefined && formatTime && (
          <div className="bg-black/60 backdrop-blur-sm text-white px-2 py-0.5 rounded-full font-mono text-xs font-bold" data-testid="timer">
            {formatTime(timeLeft)}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2 sm:gap-3">
        <Button
          onClick={toggleVideo}
          variant={isVideoEnabled ? "secondary" : "destructive"}
          size="icon"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full"
        >
          {isVideoEnabled ? <Video className="w-4 h-4 sm:w-5 sm:h-5" /> : <VideoOff className="w-4 h-4 sm:w-5 sm:h-5" />}
        </Button>
        
        <Button
          onClick={toggleAudio}
          variant={isAudioEnabled ? "secondary" : "destructive"}
          size="icon"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full"
        >
          {isAudioEnabled ? <Mic className="w-4 h-4 sm:w-5 sm:h-5" /> : <MicOff className="w-4 h-4 sm:w-5 sm:h-5" />}
        </Button>
        
        <Button
          onClick={onEndChat}
          variant="destructive"
          size="icon"
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full"
        >
          <PhoneOff className="w-5 h-5 sm:w-6 sm:h-6" />
        </Button>
        
        <Button
          onClick={toggleFullscreen}
          variant="secondary"
          size="icon"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full"
          data-testid="fullscreen-button"
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" />}
        </Button>
      </div>
    </div>
  );
};

export default RealVideoChat;
